import React, { useState } from 'react'
import ReportCalendarLoader from '../../ReportCalendarLoader'
import { toDateKey } from '../../reportCalendarDateUtils'
import '../../styles/report-calendar-reporting-timeline.sass'

const normalizeTimelineDate = (date) => {
	if (typeof date === 'string') {
		const [year, ...rest] = date.split(' ')
		return {
			year,
			dayMonth: rest.join(' '),
		}
	}

	return date
}

const getTimelineDateKey = (date) => `${date.year}-${date.dayMonth}`

const getTimelineCellKey = (companyName, cellIndex) => `${companyName}-${cellIndex}`

const parseTimelineTagLabel = (label) => {
	const separatorIndex = label.indexOf(' · ')

	if (separatorIndex === -1) {
		return {
			code: null,
			description: label,
		}
	}

	return {
		code: label.slice(0, separatorIndex),
		description: label.slice(separatorIndex + 3),
	}
}

const TimelineTagLabel = ({ label, variant }) => {
	const { code, description } = parseTimelineTagLabel(label)

	if (!code) {
		return <span className='reporting-calendar-timeline__tag-text'>{description}</span>
	}

	if (variant === 'urgent') {
		return (
			<span className='reporting-calendar-timeline__tag-text reporting-calendar-timeline__tag-text--urgent'>
				<span className='reporting-calendar-timeline__tag-code'>{code}</span>
				<span className='reporting-calendar-timeline__tag-separator' aria-hidden='true'>·</span>
				<span className='reporting-calendar-timeline__tag-description'>{description}</span>
			</span>
		)
	}

	return (
		<span className='reporting-calendar-timeline__tag-text'>
			<span className='reporting-calendar-timeline__tag-code'>{code}</span>
			<span className='reporting-calendar-timeline__tag-separator' aria-hidden='true'>·</span>
			<span className='reporting-calendar-timeline__tag-description'>{description}</span>
		</span>
	)
}

const TIMELINE_VISIBLE_TAGS_LIMIT = 3

const normalizeCellTags = (tags) => (
	(tags ?? []).flatMap((tag) => {
		if (tag.variant === 'more') {
			return tag.hiddenTags ?? []
		}

		return [tag]
	})
)

const getCellTagsView = (tags, isExpanded) => {
	const allTags = normalizeCellTags(tags)

	if (allTags.length <= TIMELINE_VISIBLE_TAGS_LIMIT || isExpanded) {
		return {
			tagsToRender: allTags,
			hiddenCount: 0,
		}
	}

	return {
		tagsToRender: allTags.slice(0, TIMELINE_VISIBLE_TAGS_LIMIT),
		hiddenCount: allTags.length - TIMELINE_VISIBLE_TAGS_LIMIT,
	}
}

const ReportCalendarReportingTimeline = ({
	dates = [],
	sourceDates = [],
	companies = [],
	canGoPrevious = false,
	canGoNext = false,
	isLoading = false,
	onPrevious,
	onNext,
	onReportSelect,
}) => {
	const [expandedCells, setExpandedCells] = useState(() => new Set())

	const toggleCellExpanded = (cellKey) => {
		setExpandedCells((current) => {
			const next = new Set(current)

			if (next.has(cellKey)) {
				next.delete(cellKey)
			} else {
				next.add(cellKey)
			}

			return next
		})
	}

	return (
		<section className='reporting-calendar-timeline'>
			<div className='reporting-calendar-timeline__header'>
				<div className='reporting-calendar-timeline__header-main'>
					<button
						type='button'
						className='reporting-calendar-timeline__nav'
						aria-label='Previous date with reports'
						onClick={onPrevious}
						disabled={isLoading || !canGoPrevious}
					/>
					<h2 className='reporting-calendar-timeline__title'>Reporting timeline</h2>
					<button
						type='button'
						className='reporting-calendar-timeline__nav reporting-calendar-timeline__nav--next'
						aria-label='Next date with reports'
						onClick={onNext}
						disabled={isLoading || !canGoNext}
					/>
				</div>
			</div>

			{isLoading ? (
				<ReportCalendarLoader minHeight={220} />
			) : (
			<div className='reporting-calendar-timeline__table'>
				<div className='reporting-calendar-timeline__row reporting-calendar-timeline__row--header'>
					<div className='reporting-calendar-timeline__row-label'>Dates</div>
					<div className='reporting-calendar-timeline__cells'>
						{dates.map((dateItem) => {
							const date = normalizeTimelineDate(dateItem)

							return (
								<div
									key={getTimelineDateKey(date)}
									className='reporting-calendar-timeline__cell reporting-calendar-timeline__cell--date'
								>
									<span className='reporting-calendar-timeline__date-year'>{date.year}</span>
									<span className='reporting-calendar-timeline__date-day-month'>{date.dayMonth}</span>
								</div>
							)
						})}
					</div>
				</div>

				{companies.map((company) => (
					<div key={company.name} className='reporting-calendar-timeline__row'>
						<div className='reporting-calendar-timeline__row-label'>{company.name}</div>
						<div className='reporting-calendar-timeline__cells'>
							{company.cells.map((tags, cellIndex) => {
								const cellKey = getTimelineCellKey(company.name, cellIndex)
								const isExpanded = expandedCells.has(cellKey)
								const { tagsToRender, hiddenCount } = getCellTagsView(tags, isExpanded)
								const hasMore = hiddenCount > 0

								return (
									<div
										key={cellKey}
										className='reporting-calendar-timeline__cell reporting-calendar-timeline__cell--tags'
									>
										{tagsToRender.map((tag) => (
											<button
												key={tag.id}
												type='button'
												className={[
													'reporting-calendar-timeline__tag',
													`reporting-calendar-timeline__tag--${tag.variant}`,
												].join(' ')}
												onClick={() => {
													const sourceDate = sourceDates[cellIndex]

													if (!sourceDate) {
														return
													}

													onReportSelect?.({
														tag,
														company: company.name,
														dateKey: toDateKey(sourceDate),
													})
												}}
											>
												<TimelineTagLabel label={tag.label} variant={tag.variant} />
											</button>
										))}
										{hasMore && (
											isExpanded ? (
												<button
													type='button'
													className='reporting-calendar-timeline__tag reporting-calendar-timeline__tag--collapse'
													onClick={() => toggleCellExpanded(cellKey)}
												>
													Collapse
												</button>
											) : (
												<button
													type='button'
													className='reporting-calendar-timeline__tag reporting-calendar-timeline__tag--more'
													onClick={() => toggleCellExpanded(cellKey)}
												>
													+{hiddenCount} more
												</button>
											)
										)}
									</div>
								)
							})}
						</div>
					</div>
				))}
			</div>
			)}
		</section>
	)
}

export default ReportCalendarReportingTimeline
